import React, { useState, useEffect } from "react";
import Header from "@/components/Header";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, User as UserIcon, KeyRound, Save } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [username, setUsername] = useState(user?.username || "");
  const [avatar, setAvatar] = useState(user?.avatar || "");
  
  const PREDEFINED_AVATARS = ["Felix", "Aneka", "Jack", "Luna", "Oliver", "Bella", "Leo", "Chloe", "Max", "Mia", "Charlie", "Lily"];

  useEffect(() => { document.title = "Tavoli da Gioco — Profilo"; }, []);
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.patch("/auth/profile", { name, avatar: avatar || null });
      await refresh();
      toast.success("Profilo aggiornato!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Le due nuove password non coincidono");
      return;
    }
    setSavingPassword(true);
    try {
      await api.patch("/auth/password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password aggiornata!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingPassword(false);
    }
  };

  const inputClass = "mt-2 bg-[#FDFBF7] dark:bg-[#1A1A1A] border-[#EAE3D9] dark:border-[#2A2A2A] dark:text-[#F5F1EA] focus-visible:ring-[#2B593F]/30 focus-visible:border-[#2B593F] rounded-xl h-11";
  const inputDisabledClass = "mt-2 bg-[#F5F1EA] dark:bg-[#141414] border-[#EAE3D9] dark:border-[#2A2A2A] dark:text-[#757169] rounded-xl h-11";
  const labelClass = "text-xs uppercase tracking-[0.1em] text-[#757169] dark:text-[#B8B2AA] font-medium";
  const hintClass = "text-[11px] text-[#757169] dark:text-[#B8B2AA] mt-1.5";

  return (
    <div className="min-h-screen bg-[#FDFBF7] dark:bg-[#181818]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link
          to="/"
          data-testid="back-to-dashboard"
          className="inline-flex items-center gap-2 text-sm text-[#2B593F] dark:text-[#8FC79F] hover:underline mb-6"
        >
          <ArrowLeft size={14} /> Torna al dashboard
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-[#2B593F] text-[#FDFBF7] flex items-center justify-center">
            <UserIcon size={20} strokeWidth={1.8} />
          </div>
          <div>
            <h1 className="font-display text-3xl font-bold text-[#2D2A26] dark:text-[#F5F1EA] tracking-tight">Il tuo profilo</h1>
            <p className="text-sm text-[#757169] dark:text-[#B8B2AA]">{user?.email}</p>
          </div>
        </div>

        <section className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl p-6 sm:p-8 mb-6">
          <h2 className="font-display text-xl font-semibold text-[#2D2A26] dark:text-[#F5F1EA] mb-1">Dati personali</h2>
          <p className="text-sm text-[#757169] dark:text-[#B8B2AA] mb-6">Aggiorna il tuo nome visualizzato e il tuo nome utente.</p>

          <form onSubmit={saveProfile} className="space-y-4">
            <div>
              <Label className={labelClass}>Nome</Label>
              <Input required value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" className={inputClass} />
            </div>
            <div>
              <Label className={labelClass}>Username</Label>
              <Input value={username} disabled data-testid="profile-username-input" className={inputDisabledClass} />
              <p className={hintClass}>L'username non può essere modificato dopo la registrazione.</p>
            </div>
            <div>
              <Label className={labelClass}>Email</Label>
              <Input value={user?.email || ""} disabled className={inputDisabledClass} />
              <p className={hintClass}>L'email non può essere modificata. Contatta un admin se necessario.</p>
            </div>
            
            <div className="pt-2">
              <Label className={labelClass}>Avatar</Label>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-3 mt-3">
                <button
                  type="button"
                  onClick={() => setAvatar("")}
                  className={`w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold transition-all border-4 ${
                    !avatar ? "border-[#2B593F] bg-[#F5F1EA] dark:bg-[#1A1A1A] text-[#2B593F]" : "border-transparent bg-[#F5F1EA] dark:bg-[#1A1A1A] text-[#757169] opacity-70 hover:opacity-100"
                  }`}
                >
                  {name ? name.charAt(0).toUpperCase() : "U"}
                </button>
                {PREDEFINED_AVATARS.map((seed) => (
                  <button
                    key={seed}
                    type="button"
                    onClick={() => setAvatar(seed)}
                    className={`w-14 h-14 rounded-full overflow-hidden transition-all border-4 ${
                      avatar === seed ? "border-[#2B593F] scale-110 shadow-md" : "border-transparent opacity-70 hover:opacity-100 hover:scale-105"
                    }`}
                  >
                    <img src={`https://api.dicebear.com/9.x/adventurer/svg?seed=${seed}`} alt={seed} className="w-full h-full bg-[#EAE3D9] dark:bg-[#2A2A2A]" />
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-4">
              <Button type="submit" disabled={savingProfile} data-testid="profile-save-button" className="bg-[#2B593F] hover:bg-[#1E3F2C] text-[#FDFBF7] rounded-xl h-11 px-6 font-medium">
                <Save size={16} className="mr-2" />
                {savingProfile ? "Salvataggio..." : "Salva modifiche"}
              </Button>
            </div>
          </form>
        </section>

        <section className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 rounded-lg bg-[#F4DAD3] dark:bg-[#2E1A18] text-[#C8553D] dark:text-[#F08A78] flex items-center justify-center">
              <KeyRound size={16} strokeWidth={1.8} />
            </div>
            <h2 className="font-display text-xl font-semibold text-[#2D2A26] dark:text-[#F5F1EA]">Cambia password</h2>
          </div>
          <p className="text-sm text-[#757169] dark:text-[#B8B2AA] mb-6">Per sicurezza ti chiediamo la password attuale.</p>

          <form onSubmit={savePassword} className="space-y-4">
            <div>
              <Label className={labelClass}>Password attuale</Label>
              <Input type="password" required value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} data-testid="current-password-input" className={inputClass} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className={labelClass}>Nuova password</Label>
                <Input type="password" required minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="new-password-input" placeholder="Almeno 8 caratteri" className={inputClass} />
              </div>
              <div>
                <Label className={labelClass}>Conferma nuova password</Label>
                <Input type="password" required minLength={6} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="confirm-password-input" className={inputClass} />
              </div>
            </div>
            <div className="pt-2">
              <Button type="submit" disabled={savingPassword} data-testid="change-password-button" className="bg-[#C8553D] hover:bg-[#A6432E] text-[#FDFBF7] rounded-xl h-11 px-6 font-medium">
                <KeyRound size={16} className="mr-2" />
                {savingPassword ? "Aggiornamento..." : "Aggiorna password"}
              </Button>
            </div>
          </form>
        </section>
      </main>
    </div>
  );
}
